import { useCallback, useMemo, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly, formatDateTime } from '../../../../lib/formatDate';
import { surveyTrainingRecommendationsApi } from '../surveyApi';
import { TRAINING_DECISION, TRAINING_DECISION_LABELS, type SurveyTrainingRecommendationPersonnelRow } from '../surveyTypes';
import { SurveyPlanAddPersonnelModal } from './SurveyPlanAddPersonnelModal';
import { MemberAvatar } from './SurveyPlanMembers';
import { SurveyTrainingTrackModal } from './SurveyTrainingTrackModal';
import { useMemberNames } from './useMemberNames';

/**
 * One employee's status on the recommendation, the LATEST step they have reached — every arm a
 * fact the row already carries, nothing stored for it:
 *
 *   withheld (above the reader's level) · not sent yet · pending · declined ·
 *   accepted · enrolled (they named the school) · training started (they recorded their training) · training completed or cancelled.
 *
 * A submitted report is not a status here: it has its own column beside this one.
 * `!= null` throughout — off the wire an absent value is undefined.
 */
function trackStatus(row: SurveyTrainingRecommendationPersonnelRow): string {
  if (row.isRestricted) return 'Withheld';
  if (!row.isSent) return 'Not sent yet';
  if (row.decision !== TRAINING_DECISION.Accepted) return TRAINING_DECISION_LABELS[row.decision];
  // Ended either way — the result (1 = Cancelled) says which.
  if (row.trainingCompletedAt != null) return row.trainingResult === 1 ? 'Training cancelled' : 'Training completed';
  if (row.trainingStartDate != null) return 'Training started';
  if (row.enrolledAt != null) return 'Enrolled';

  return TRAINING_DECISION_LABELS[row.decision];
}

/**
 * A training recommendation's Personnel — the STANDARD panel table (product call, 13 Oct 2026; it
 * was a card listing names): the employees on its plan, each with where they are on it — their
 * answer, their training's dates, when they completed it and when they reported.
 *
 * - **Create** adds personnel: assessed employees with no plan yet are put on the plan (and so on
 *   this recommendation). It writes their plans, so it is `surveys.plan`.
 * - **Name** is the row's anchor and opens that employee's track, read only — everything on it is
 *   the employee's to write, from Self Service.
 * - Names are resolved in ONE batch and never stored; above the reader's access level a row reads
 *   Restricted, its track is withheld by the SERVER, and opening it is refused there.
 *
 * NO checkboxes and no row menu: nothing can be done to a row from here. The search reaches the
 * employee NUMBER only — a name lives in another store the server cannot search.
 */
export function SurveyTrainingRecommendationPersonnelPanel({
  recommendationId,
  surveyId,
  planId,
  planTitle,
  readPermission,
  canAdd,
  onChanged,
}: {
  recommendationId: string;
  surveyId: string;
  planId: string;
  planTitle: string;
  /** The key the page was reached on — `surveys.read`, or the register's own read. A track answers either. */
  readPermission: string;
  /** False where the recommendation is deleted — Create is then disabled, with the reason. */
  canAdd: boolean;
  /** After personnel are added — the page re-reads its count. */
  onChanged: () => void;
}) {
  const canReadSurveys = usePermission('surveys.read');
  const [adding, setAdding] = useState(false);
  const [viewing, setViewing] = useState<{ respondentId: string; name: string } | null>(null);

  const fetchPage = useCallback(
    (query: DataTableQuery, signal: AbortSignal) => surveyTrainingRecommendationsApi.personnel(recommendationId, query, signal),
    [recommendationId],
  );

  const state = useDataTable<SurveyTrainingRecommendationPersonnelRow>({ fetchPage, pageSize: 10 });
  const nameOf = useMemberNames(state.rows);

  const columns = useMemo<DataColumn<SurveyTrainingRecommendationPersonnelRow>[]>(
    () => [
      {
        key: 'name',
        header: 'Name',
        width: 260,
        // Resolved for this page from another database, so neither sortable nor searchable.
        render: (row) => (
          <span className="flex min-w-0 items-center gap-2.5">
            <MemberAvatar personId={row.personId} name={nameOf(row)} />
            <TextCell value={nameOf(row)} />
          </span>
        ),
      },
      { key: 'employeeNumber', header: 'Employee No.', width: 160, summary: true, render: (row) => <TextCell value={row.employeeNumber ?? null} mono /> },
      {
        /*
          Where they are on it — their own lifecycle, not whether the row is live. "Status" rather
          than "Answer" (product call, 13 Oct 2026): it goes on past the answer, to the training
          started and the training completed. A `stage` column, so it is NOT the shell's
          live-or-deleted `status` flag — this table has no such column.
        */
        key: 'trackStatus',
        header: 'Status',
        width: 190,
        stage: true,
        summary: true,
        render: (row) => <StatusText tone="inactive">{trackStatus(row)}</StatusText>,
      },
      {
        // Two calendar dates as one range — `formatDateOnly`, never the instant path.
        key: 'training',
        header: 'Training',
        width: 230,
        render: (row) => (
          <TextCell
            value={
              row.trainingStartDate != null && row.trainingEndDate != null
                ? `${formatDateOnly(row.trainingStartDate)} – ${formatDateOnly(row.trainingEndDate)}`
                : null
            }
            mono
          />
        ),
      },
      {
        key: 'completed',
        header: 'Ended',
        width: 190,
        render: (row) => <TextCell value={row.trainingCompletedAt != null ? formatDateTime(row.trainingCompletedAt) : null} mono />,
      },
      {
        key: 'report',
        header: 'Report Submitted',
        width: 190,
        render: (row) => <TextCell value={row.reportAt != null ? formatDateTime(row.reportAt) : null} mono />,
      },
      {
        key: 'result',
        header: 'Result',
        width: 120,
        render: (row) => <TextCell value={row.impactPassed == null ? null : row.impactPassed ? 'Passed' : 'Failed'} />,
      },
      {
        key: 'impact',
        header: 'Impact Assessed',
        width: 190,
        render: (row) => <TextCell value={row.impactAssessmentAt != null ? formatDateTime(row.impactAssessmentAt) : null} mono />,
      },
    ],
    [nameOf],
  );

  return (
    // The tab is `bleed`, so the inset is this wrapper's — as on the plan's own table tab.
    <div className="flex h-full min-w-0 flex-col p-4">
      <DataTablePanel<SurveyTrainingRecommendationPersonnelRow>
        id="training-recommendation-personnel"
        state={state}
        columns={columns}
        rowKey={(row) => row.respondentId}
        searchPlaceholder="Search employee number"
        fill
        // Survey's write, behind Survey's lists — both keys, or the form would open onto a refusal.
        onCreate={canReadSurveys ? () => setAdding(true) : undefined}
        createLabel="Add personnel"
        createPermission="surveys.plan"
        createDisabledReason={canAdd ? null : 'This training recommendation is deleted.'}
        // A read-only register of the employees' own tracks: nothing can be done to a row.
        selectable={false}
        // The row is a plan member, not a record with its own creation — no provenance columns.
        auditColumns={false}
        // The NAME opens that employee's track. A Restricted row opens onto the server's refusal.
        onRowOpen={(row) => setViewing({ respondentId: row.respondentId, name: nameOf(row) })}
        openPermission={readPermission}
        emptyMessage="No employee is on this recommendation's plan yet."
        emptySearchMessage="No employee number matches that search."
      />

      {adding ? (
        <SurveyPlanAddPersonnelModal
          open
          surveyId={surveyId}
          planId={planId}
          planTitle={planTitle}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            state.reload();
            onChanged();
          }}
        />
      ) : null}

      {viewing ? (
        <SurveyTrainingTrackModal
          recommendationId={recommendationId}
          respondentId={viewing.respondentId}
          employeeName={viewing.name}
          onClose={() => setViewing(null)}
        />
      ) : null}
    </div>
  );
}
